/**
 * Create Challenge UI Helper Functions
 * Utility functions for UI display and formatting
 */

import { formatGameDateOnly, formatWeekday, formatShortDate, formatTime12h } from '../../../helpers/dateFormatters.helper';

/**
 * True when the value is an http(s) or protocol-relative image URL (OAuth/CDN shapes).
 */
export const isAvatarUrl = (avatar: string): boolean => {
  if (avatar == null || typeof avatar !== 'string') return false;
  const t = avatar.trim();
  if (!t) return false;
  if (t.startsWith('//')) return true;
  const lower = t.toLowerCase();
  return lower.startsWith('http://') || lower.startsWith('https://');
};

/**
 * Normalize URL for <Image source={{ uri }} /> (protocol-relative → https).
 */
export const normalizeAvatarImageUri = (uri: string): string => {
  const t = uri.trim();
  if (t.startsWith('//')) return `https:${t}`;
  return t;
};

/**
 * Check if name is invalid (URL, too long, or undefined)
 */
export const isInvalidName = (name: string | undefined): boolean => {
  if (!name) return true;
  const t = name.trim();
  if (t.startsWith('//')) return true;
  const lower = t.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
  if (name.length > 80) return true;
  return false;
};

/**
 * Get initials from a name
 */
export const getInitials = (name: string): string => {
  if (!name || isInvalidName(name)) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Get safe display name, falling back to email prefix or default
 */
export const getSafeDisplayName = (name: string, fallback?: string): string => {
  if (!isInvalidName(name)) return name;
  if (fallback && !fallback.includes('@placeholder.com')) {
    return fallback.split('@')[0];
  }
  return 'Friend';
};

export { formatGameDateOnly } from '../../../helpers/dateFormatters.helper';

/**
 * Format game date and time for match header (e.g. "Wed 5 Feb • 3:30 PM").
 * Uses dateStr (YYYY-MM-DD) and timeStr (HH:mm or HH:mm:ss).
 * If timeStr is missing/invalid, returns date-only (e.g. "Wed 5 Feb").
 */
export const formatGameDateAndTime = (
  dateStr: string,
  timeStr: string
): string => {
  if (!dateStr) return '';
  const timePart = (timeStr || '').trim();
  const looksLikeTime = /^\d{1,2}:\d{2}(:\d{2})?$/.test(timePart);
  if (!looksLikeTime) return formatGameDateOnly(dateStr);
  const parts = dateStr.split('-').map(Number);
  const timeParts = timePart.split(':').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  const hh = timeParts[0] ?? 0;
  const mm = timeParts[1] ?? 0;
  if (y == null || m == null || d == null) return '';
  const date = new Date(y, m - 1, d, hh, mm);
  if (Number.isNaN(date.getTime())) return formatGameDateOnly(dateStr);
  return `${formatWeekday(date)} ${formatShortDate(date)} • ${formatTime12h(date)}`;
};

/**
 * Return date and time as separate lines for match header (e.g. dateLine: "Wed 5 Feb", timeLine: "3:30 PM").
 * Uses dateStr (YYYY-MM-DD) and timeStr (HH:mm or HH:mm:ss). Returns empty strings when invalid.
 */
export const getGameDateAndTimeLines = (
  dateStr: string,
  timeStr: string
): { dateLine: string; timeLine: string } => {
  if (!dateStr) return { dateLine: '', timeLine: '' };
  const timePart = (timeStr || '').trim();
  const looksLikeTime = /^\d{1,2}:\d{2}(:\d{2})?$/.test(timePart);
  const parts = dateStr.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null) {
    return { dateLine: formatGameDateOnly(dateStr), timeLine: '' };
  }
  const timeParts = timePart.split(':').map(Number);
  const hh = timeParts[0] ?? 0;
  const mm = timeParts[1] ?? 0;
  const date = new Date(y, m - 1, d, hh, mm);
  if (Number.isNaN(date.getTime())) {
    return { dateLine: formatGameDateOnly(dateStr), timeLine: '' };
  }
  const dateLine = `${formatWeekday(date)} ${formatShortDate(date)}`;
  const timeLine = looksLikeTime ? formatTime12h(date) : '';
  return { dateLine, timeLine };
};
